import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject, OnInit, signal, viewChild } from '@angular/core';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatIcon } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { TypeCBComponent } from '../../../../utils/component/type-filter-combobox/type-cb.component';
import { FreeFilterInputComponent } from '../../../../utils/component/free-filter-input/free-filter-input.component';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { getStatusText, StateFilterCBComponent } from '../../../../utils/component/state-filter-combobox/state-cb.component';
import { AsyncPipe, DatePipe } from '@angular/common';
import { MatIconButton, MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ConfirmDialogComponent } from '../../../../core/components/confirm-delete-dialog/confirm-dialog.component';
import { EditExamComponent } from '../../edit-exam/edit-exam/edit-exam.component';
import { Store } from '@ngxs/store';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import { Approval, ExerciseKind, getExerciseKinds } from '../../../../core/types';
import { AppSelectors } from '../../../../app.state';
import { TranslateComponent } from '../../../../core/components/translation/translation.component';
import { PlayerMessageService } from '../../../../core/services/player.service';
import { PreviewApiService } from '../../../../core/services/preview-api.service';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { BehaviorSubject, combineLatest, firstValueFrom, from, map, Observable, switchMap, tap } from 'rxjs';
import { LocalStorageService } from '../../../../core/services/local-storage.service';
import { IFilters } from '@datx/jsonapi/interfaces/IFilters';
import { ConsumerEntity } from '../../../../consumer/consumer.entity';
import { ConsumerEntityService } from '../../../../consumer/services/consumer.entity.service';
import { SimpleConfirmData } from '../../../../core/components/confirm-dialog-raw/confirm-dialog-raw.component';
import { ContentEntityComponent, ContentEntityDialogData } from '../../../../core/components/content-entity/content-entity.component';
import { WidgetMessageService } from '../../../../core/services/widget-message.service';
import { EntityStateModel, InvalidateLoadedQueries } from '../../../../core/state/entity.state';
import { SubjectEntityService } from '../../../../subject/services/subject.entity.service';
import { SubjectEntity } from '../../../../subject/subject.entity';
import { TableLoadingSpinnerComponent } from '../../../../utils/component/table-loading-spinner/table-loading-spinner';
import { makeQueryKey } from '../../../../utils/entity-states/query-key';
import { ExamEntity } from '../../exam.entity';
import { ExamEntityService } from '../../services/exam.entity.service';
import { HasApprovalState, StateBadgeComponent } from '../../../../core/components/state-badge/state-badge.component';
import { BaseFilterComponent } from '../../../../utils/component/base-filter-component/base-filter-component';

@Component({
  selector: 'pixl-exam',
  imports: [
    MatIcon,
    MatSortModule,
    MatIconButton,
    MatTableModule,
    MatButtonModule,
    MatSelectModule,
    MatTooltipModule,
    MatDialogModule,
    MatListModule,
    MatInputModule,
    DatePipe,
    TranslateComponent,
    TypeCBComponent,
    FreeFilterInputComponent,
    StateFilterCBComponent,
    TranslocoModule,
    MatPaginator,
    AsyncPipe,
    TableLoadingSpinnerComponent,
    StateBadgeComponent
  ],
  templateUrl: './exam.component.html',
  styleUrl: './exam.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ExamComponent extends BaseFilterComponent implements OnInit, AfterViewInit {
  sort = viewChild(MatSort);
  totalItems = 1000;

  private readonly store = inject(Store);
  public examService = inject(ExamEntityService);
  private consumerService = inject(ConsumerEntityService);
  private subjectService = inject(SubjectEntityService);
  private widgetService = inject(WidgetMessageService);
  private previewApiService = inject(PreviewApiService);
  exams$!: Observable<ExamEntity[]>;
  private dialog = inject(MatDialog);
  deletedIds$ = new BehaviorSubject<string[]>([]);
  public refresh$ = new BehaviorSubject<void>(undefined);
  private readonly pageChange$ = toObservable(this.pageChange);

  consumers$!: Observable<ConsumerEntity[]>;
  subjects$!: Observable<SubjectEntity[]>;

  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);
  private playerService = inject(PlayerMessageService);

  public filters: IFilters = {};
  private sortString = 'name';

  private _liveAnnouncer = inject(LiveAnnouncer);
  private appState$ = this.store.select(AppSelectors.appState);
  private LIST_FIELDS: string[] | null = ['name', 'kind', 'approval', 'consumerId', 'subjectId', 'consumerData', 'config', 'consumer'];
  private translocoService = inject(TranslocoService);
  private localStorageService = inject(LocalStorageService);

  public dataSource = new MatTableDataSource<ExamEntity>([] as ExamEntity[]);
  public displayedColumns: string[] = ['edit-actions', 'id', 'kind', 'name', 'consumer', 'competency', 'updated', 'approval'];
  public kinds: ExerciseKind[] = [];
  public searchFilterHint = signal(false);
  public readonly coloredButtonsEnabled = this.localStorageService.coloredButtonsEnabled;
  public readonly examStateClass = ExamEntity as unknown as new () => HasApprovalState;

  ngOnInit() {
    this.kinds = getExerciseKinds();
    this.loadRelationData();

    this.exams$ = combineLatest([
      this.pageChange$,
      this.refresh$,
      this.deletedIds$
    ]).pipe(
      switchMap(([{ pageIndex, pageSize }, , deletedIds]) => {
        const query: Record<string, string | number> = {
          'include': 'consumers,subjects',
          'page[size]': pageSize,
          'page[number]': pageIndex + 1,
          'sort': this.sortString,
        };

        if (this.freeFilter?.trim()?.length >= this.freeFilterLen) {
          query['filter[idOrNameLike]'] = this.freeFilter.trim();
        }

        if (this.statusFilter?.length > 0) {
          query['filter[approval]'] = this.statusFilter.trim();
        }

        if (this.consumerFilter?.length > 0) {
          query['filter[consumers][id]'] = this.consumerFilter.join(',');
        }

        if (this.subjectFilter?.length > 0) {
          query['filter[subjectId]'] = this.subjectFilter.join(',');
        }

        if (this.exerciseKindFilter?.length > 0) {
          query['filter[kind]'] = this.exerciseKindFilter.trim();
        }

        return from(this.examService.ensureLoaded(query)).pipe(
          switchMap(() => this.store.select(this.examService.allByQuery(query))),
          map((entities: ExamEntity[]) => entities.filter(m => !deletedIds.includes(m.id))),
          tap(() => {
            const state = this.store.selectSnapshot(this.examService.stateClass) as EntityStateModel<ExamEntity>;
            const queryKey = makeQueryKey(query);
            this.totalItems = state?.meta?.[queryKey]?.page?.total ?? 0;
            this.cdr.markForCheck();
          })
        );
      })
    );
  }

  ngAfterViewInit(): void {
    this.paginator()?.page.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event: PageEvent) => {
      this.pageChange.set({ pageIndex: event.pageIndex, pageSize: event.pageSize });
    });
  }

  private loadRelationData(): void {
    const consumerQuery = { sort: 'name' };
    this.consumers$ = from(this.consumerService.ensureLoaded(consumerQuery)).pipe(
      switchMap(() => this.store.select(this.consumerService.allByQuery(consumerQuery)))
    );

    const subjectQuery = { sort: 'name' };
    this.subjects$ = from(this.subjectService.ensureLoaded(subjectQuery)).pipe(
      switchMap(() => this.store.select(this.subjectService.allByQuery(subjectQuery)))
    );
  }

  addExam(combinedKind: string): void {
    const [kind, subKind] = combinedKind.split('.');
    const dialogRef = this.dialog.open(EditExamComponent, {
      width: '500px',
      data: {
        kind: kind,
        copyRestricted: false,
        name: '',
        config: subKind ? { exerciseSubKind: subKind } : undefined,
      }
    });

    dialogRef.afterClosed().subscribe(async (result: Partial<ExamEntity> | undefined) => {
      if (result) {
        const newEntityInstance = Object.assign(new ExamEntity(), result);
        await this.examService.create(newEntityInstance);
        this.store.dispatch(new InvalidateLoadedQueries());
        this.refresh$.next();

        this.widgetService.setMessage({
          message: this.translocoService.translate('task_created'),
          messageTye: 'mtSuccess'
        });
      }
    });
  }

  public async copyExam(exam: ExamEntity) {
    const copyEntityInstance = Object.assign(new ExamEntity(), exam);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (copyEntityInstance as any).id = undefined;
    copyEntityInstance.name = `${copyEntityInstance.name} (Kopie)`;
    copyEntityInstance.version = 1;
    await this.examService.create(copyEntityInstance);
    this.store.dispatch(new InvalidateLoadedQueries());
    this.refresh$.next();

    this.widgetService.setMessage({
      message: this.translocoService.translate('task_cloned'),
      messageTye: 'mtSuccess'
    });
  }

  public async deleteExam(exam: ExamEntity): Promise<void> {
    if (!exam?.id) return;
    this.currentSelectedRow.set(exam.id);

    const confirmed = await firstValueFrom(
      this.dialog.open<ConfirmDialogComponent, SimpleConfirmData, boolean>(ConfirmDialogComponent, {
        data: {
          name: String(exam.name),
          headerText: this.translocoService.translate('task_delete'),
        }
      }).afterClosed()
    );

    if (!confirmed) return;

    await firstValueFrom(this.examService.delete(exam.id));
    this.store.dispatch(new InvalidateLoadedQueries());
    this.refresh$.next();

    this.widgetService.setMessage({
      message: this.translocoService.translate('task_deleted'),
      messageTye: 'mtSuccess'
    });
  }

  editExam(exam: ExamEntity): void {
    this.currentSelectedRow.set(exam.id);
    const dialogRef = this.dialog.open(EditExamComponent, {
      width: '800px',
      data: {
        ...exam,
        consumerExtra: this.getConsumer(exam.consumerId)
      },
    });

    dialogRef.afterClosed().subscribe(async (updated: ExamEntity | undefined) => {
      if (!updated) return;
      await firstValueFrom(this.examService.update(updated));
      this.refresh$.next();

      this.widgetService.setMessage({
        message: this.translocoService.translate('task_saved'),
        messageTye: 'mtSuccess'
      });
    });
  }

  public editContentExam(exam: ExamEntity) {
    this.currentSelectedRow.set(exam.id);
    const dialogRef = this.dialog.open(ContentEntityComponent, {
      minWidth: '98vw',
      height: '98vh',
      data: {
        type: 'exam',
        model: exam
      } as ContentEntityDialogData
    });

    dialogRef.afterClosed().subscribe(async (updated: ExamEntity | undefined) => {
      if (!updated) return;
      await firstValueFrom(this.examService.update(updated));
      this.refresh$.next();

      this.widgetService.setMessage({
        message: this.translocoService.translate('task_saved'),
        messageTye: 'mtSuccess'
      });
    });
  }

  public announceSortChange(sortState: Sort) {
    if (sortState.direction) {
      this._liveAnnouncer.announce(`Sorted ${sortState.direction}ending`);
    }
    else {
      this._liveAnnouncer.announce('Sorting cleared');
    }
    this.sortData(sortState);
  }

  public sortData(sort: Sort): void {
    this.sortString = sort.direction === 'desc' ? `-${sort.active}` : sort.active;
    this.refresh$.next();
  }

  public getCreatedDate(id: string): Date {
    return new Date(parseInt(id.substring(0, 8), 16) * 1000);
  }

  public getStateText(approval: Approval): string {
    return this.translocoService.translate(getStatusText(approval));
  }

  getConsumer(consumerId: string, getName = false): ConsumerEntity | string {
    if (!consumerId) return '---';
    const state = this.store.selectSnapshot(this.consumerService.stateClass);
    const consumer = this.consumerService.stateClass.byId(state)(consumerId);
    return getName ? (consumer?.name || consumerId) : consumer;
  }

  getSubject(subjectId: string, getName = false): SubjectEntity | string {
    if (!subjectId) return '---';
    const state = this.store.selectSnapshot(this.subjectService.stateClass);
    const subject = this.subjectService.stateClass.byId(state)(subjectId);
    return getName ? (subject?.name || subjectId) : subject;
  }

  public previewPlayer2(exam: ExamEntity) {
    this.currentSelectedRow.set(exam.id);
    this.previewApiService.upsertPreview('exam', exam.id, exam).subscribe({
      next: () => this.playerService.openPlayerPreview(exam.consumerId, 'exam', exam.id),
      error: () => this.widgetService.setMessage({
        message: this.translocoService.translate('preview.saveError'),
        messageTye: 'mtError'
      })
    });
  }
}
